import clsx from 'clsx';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import useEventTracking from '@hooks/useEventTracking';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import { Newsletter } from '@interfaces/homepage';
import { newsletter_list_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { FormLocation, LeadTypes } from '@utils/enums';

import { EXPLORE_OUR_NEWSLETTERS_TEXT, SEE_ALL_NEWSLETTERS_TEXT } from './constants';

interface NewsletterSignupCardProps {
  className?: string;
  leadType: LeadTypes;
  newsletter: Newsletter;
  redirectToOnSuccess: string;
  splitOnTablet?: boolean;
}

const NewsletterSignupCard = ({
  className,
  leadType,
  newsletter: { description, id: newsletterId, isLoggedIn, isSubscribed, name },
  redirectToOnSuccess,
  splitOnTablet = false,
}: NewsletterSignupCardProps) => {
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.TITVNewsletterSignup,
  });

  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { mutate: subscribeToNewsletter, isLoading: isSubscribeLoading } =
    useSubscribeToNewsletter();

  const trackNewsletterLinkClick = (clickText: string) =>
    trackEvent(click({ clickTarget: ClickTarget.ViewAllNewsletters, clickText }));

  const handleSubscribe = () =>
    subscribeToNewsletter(
      { newsletter_id: newsletterId },
      {
        onSuccess: () => {
          setSuccessfullySubscribed(true);
          toast.success(`Thank you for signing up for ${name}.`);
        },
        onError: () => {
          toast.error(`There was a problem signing you up for ${name}, please try again later`);
        },
      }
    );

  if (isSubscribed || successfullySubscribed) {
    return (
      <Track>
        <aside
          className={clsx(className, 'text-center')}
          data-testid="titv-rail-newsletter-explore"
        >
          <a
            href={newsletter_list_path()}
            className="mt-32 inline-block font-suisse-intl text-button-sm text-primary-magenta underline transition-colors hover:text-primary-pink dark:text-primary-pink dark:hover:text-primary-magenta"
            onClickCapture={() => trackNewsletterLinkClick(EXPLORE_OUR_NEWSLETTERS_TEXT)}
          >
            {EXPLORE_OUR_NEWSLETTERS_TEXT}
          </a>
        </aside>
      </Track>
    );
  }

  return (
    <Track>
      <aside
        className={clsx(className, 'border-0 border-t border-solid border-primary-black')}
        data-testid="titv-rail-newsletter-signup"
      >
        <div
          className={clsx('flex flex-col', {
            'sm:max-md:flex-row sm:max-md:items-start sm:max-md:gap-32': splitOnTablet,
          })}
        >
          <div className={clsx({ 'sm:max-md:w-1/2': splitOnTablet })}>
            <h2 className="mb-0 mt-32 font-suisse-neue text-body-md text-primary-black lg:text-subheadline-sm">
              {name} Newsletter
            </h2>
            <p className="my-10 font-suisse-screen text-disclosure-md text-primary-black">
              {description}
            </p>
          </div>
          <div
            className={clsx('flex flex-col', {
              'sm:max-md:mt-32 sm:max-md:w-1/2': splitOnTablet,
            })}
          >
            {isLoggedIn ? (
              <PrimaryButton
                as="button"
                className="w-full px-24 py-[9px]"
                disabled={isSubscribeLoading}
                onClick={handleSubscribe}
              >
                Sign up
              </PrimaryButton>
            ) : (
              <EmailLeadCapture
                buttonText="Sign up"
                buttonClassName="w-full"
                formClassName="*:flex-col *:gap-8 *:*:*:*:!border-grayscale-gray"
                formLocation={FormLocation.TITV}
                gdprClassName="md:items-start flex-col lg:flex-row lg:items-center mb-8"
                inputClassName="w-full dark:bg-[var(--light-grayscale-charcoal)]"
                leadType={leadType}
                newsletterIds={[Number(newsletterId)]}
                redirectToOnSuccess={redirectToOnSuccess}
                toastMessageOnSuccess={`Thank you for signing up for ${name}.`}
              />
            )}
            <TextButton
              as="link"
              className="mt-16 w-fit max-sm:!w-fit"
              href={newsletter_list_path()}
              size="small"
              onClickCapture={() => trackNewsletterLinkClick(SEE_ALL_NEWSLETTERS_TEXT)}
            >
              {SEE_ALL_NEWSLETTERS_TEXT}
            </TextButton>
          </div>
        </div>
      </aside>
    </Track>
  );
};

export default NewsletterSignupCard;
